import { useNavigate, useSearch } from "@tanstack/react-router";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useConvertRequestToEvent,
  useCreateEvent,
  useRequestQuery,
} from "../api/queries";
import { buildRequestEventDatePrefill } from "../../domain/services/request-date-fields";
import { eventFeaturesFromPreset } from "../lib/event-features-ui";
import { serializeEventForm } from "../lib/serialize-event-form";
import { EventForm } from "../ui/EventForm";

export function EventCreatePage() {
  const nav = useNavigate({ from: "/app/performance/events/create" });
  const search = useSearch({ from: "/app/performance/events/create" }) as {
    requestId?: string;
  };
  const createEvent = useCreateEvent();
  const requestId = search.requestId;
  const { data: request, isLoading: isRequestLoading } = useRequestQuery(
    requestId ?? "",
  );
  const convertRequest = useConvertRequestToEvent(requestId ?? "");
  const isConverting = !!requestId;

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            {
              label: isConverting ? "ແປງຄຳຂໍເປັນງານສະແດງ" : "ສ້າງໃໝ່",
            },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6">
          <h2 className="font-bold text-2xl tracking-tight">
            {isConverting ? "ແປງຄຳຂໍເປັນງານສະແດງ" : "ສ້າງງານສະແດງ"}
          </h2>
          <p className="text-muted-foreground">
            {isConverting
              ? "ກວດສອບ ແລະ ປັບຂໍ້ມູນກ່ອນສ້າງງານສະແດງຈາກຄຳຂໍ."
              : "ສ້າງງານສະແດງໃໝ່ໃນລະບົບ."}
          </p>
        </div>

        {isConverting && isRequestLoading ? (
          <p className="text-muted-foreground">ກຳລັງໂຫຼດຄຳຂໍ...</p>
        ) : isConverting && !request ? (
          <p className="text-destructive">ບໍ່ພົບຄຳຂໍທີ່ຈະແປງ</p>
        ) : (
          <div className="rounded-xl border bg-card px-6 pt-6">
            <EventForm
              excludeRequestId={requestId}
              initialValues={
                request
                  ? (() => {
                      const { eventDate, endDate } = buildRequestEventDatePrefill({
                        dateMode: request.dateMode,
                        requestedEventDate: request.requestedEventDate,
                        showDates: request.showDates,
                      });
                      return {
                        title: request.title,
                        status: "confirmed",
                        featurePreset: "performance_show" as const,
                        eventFeatures: eventFeaturesFromPreset("performance_show"),
                        categoryId: request.categoryId ?? "",
                        eventDate,
                        endDate,
                        locationText: null,
                        notes: request.notes,
                      };
                    })()
                  : undefined
              }
              onSubmit={async (vals) => {
                try {
                  const payload = serializeEventForm(vals);
                  const created = request
                    ? (
                        await convertRequest.mutateAsync({
                          ...payload,
                          priorityOrder: request.priorityOrder,
                        })
                      ).event
                    : await createEvent.mutateAsync(payload);
                  nav({
                    to: "/app/performance/events/$id",
                    params: { id: created.id },
                  });
                } catch {}
              }}
              onCancel={() =>
                request
                  ? nav({
                      to: "/app/performance/requests/$id",
                      params: { id: request.id },
                    })
                  : nav({ to: "/app/performance/events" })
              }
              submitting={createEvent.isPending || convertRequest.isPending}
            />
          </div>
        )}
      </Main>
    </>
  );
}
